import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications, SimpleModal, Box } from '@sequencehq/core-components'
import { ARCHIVE_QUOTE_SIGNATURE_WARNING } from './archiveQuote.constants'

/**
 * Displays a modal which allows for archiving the quote.
 */
export const ArchiveQuoteModal = (props: {
  quoteId: string
  isOpen?: boolean
  onClose?: () => void
  onSuccess?: () => void | Promise<void>
}) => {
  const hook = useArchiveQuoteModal({
    quoteId: props.quoteId,
    onSuccess: props.onSuccess
  })

  return (
    <SimpleModal
      variant="dialog"
      data-testid="archiveQuoteModal"
      isOpen={Boolean(props.isOpen)}
      onClose={() => props.onClose?.()}
      title="Archive quote"
      submitButtonText="Archive"
      submittingButtonText="Archiving..."
      isDestructive
      disableSubmitOnEnter
      onSubmit={hook.archive}
    >
      {hook.showSignatureWarning ? (
        <>
          <Box data-testid="archiveQuoteModal.signatureWarning">
            This will archive the quote immediately.{' '}
            {ARCHIVE_QUOTE_SIGNATURE_WARNING}
          </Box>
          <Box marginTop="12px">You can still duplicate archived quotes.</Box>
        </>
      ) : (
        <>
          <Box>This will archive the quote immediately.</Box>
          <Box marginTop="12px">You can still duplicate archived quotes.</Box>
        </>
      )}
    </SimpleModal>
  )
}

const useArchiveQuoteModal = (props: {
  quoteId: string
  onSuccess?: () => void | Promise<void>
}) => {
  const { displayNotification } = useNotifications()
  const quoteRes = useQuery(
    dashboard20240730Client.getQuote,
    { id: props.quoteId },
    {
      select: data => {
        if (!data) {
          return null
        }
        return {
          quoteNumber: data.quoteNumber,
          status: data.status
        }
      }
    }
  )

  const archiveQuoteMutator = useMutation(
    dashboard20240730Client.postQuoteArchive
  )

  return {
    showSignatureWarning: quoteRes.data?.status == 'READY_TO_SIGN',
    archive: async () => {
      const res = await archiveQuoteMutator.mutateAsync({
        id: props.quoteId
      })

      if (!res) {
        displayNotification('Could not archive quote', { type: 'error' })
        return false
      }

      if (props.onSuccess) {
        await props.onSuccess()
      }

      displayNotification(`Quote ${quoteRes.data?.quoteNumber} archived`, {
        type: 'success'
      })
      return true
    }
  }
}
